'use strict'
import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { CircularProgress } from '@material-ui/core'
import { getCrewColor } from './crewColors'
import {
  applyBasemapProvider,
  getLatestMaricopaProvider
} from '../../HerbicideSchedule/v3/maricopaBasemap'

const ROUTE_MAP_STYLE = { width: '100%', height: 360 }
const DEFAULT_CENTER = [33.45, -112.07]

export default function CrewDayRouteDetail ({
  url,
  selectedScenarioId,
  crewId,
  crewName,
  date,
  crewIds,
  onSnackbar
}) {
  const mapContainerRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const [routeData, setRouteData] = useState(null)
  const [loading, setLoading] = useState(false)

  const crewColor = getCrewColor(crewId, crewIds)

  const fetchRoute = useCallback(async () => {
    if (!crewId || !date) return
    setLoading(true)
    try {
      const basePath = selectedScenarioId
        ? `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/crews/${crewId}/route`
        : `${url}/api/hmi/territory/crews/${crewId}/route`
      const response = await axios.get(basePath, { params: { date } })
      setRouteData(response.data || null)
    } catch (err) {
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to load route', 'error')
      setRouteData(null)
    } finally {
      setLoading(false)
    }
  }, [url, selectedScenarioId, crewId, date, onSnackbar])

  useEffect(() => { fetchRoute() }, [fetchRoute])

  useEffect(() => {
    if (!mapContainerRef.current || !routeData?.stops?.length) return
    if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null }

    const map = L.map(mapContainerRef.current, {
      center: DEFAULT_CENTER, zoom: 11,
      zoomControl: true, attributionControl: false
    })
    mapInstanceRef.current = map
    applyBasemapProvider(map, getLatestMaricopaProvider())

    const routeCoords = routeData?.route?.geometry?.coordinates || []
    if (routeCoords.length >= 2) {
      const latlngs = routeCoords.map(([lng, lat]) => [lat, lng])
      L.polyline(latlngs, { color: crewColor, weight: 4, opacity: 0.8 }).addTo(map)
    }

    const bounds = []
    routeData.stops.forEach(stop => {
      if (!Number.isFinite(stop.latitude) || !Number.isFinite(stop.longitude)) return
      if (stop.latitude === 0 && stop.longitude === 0) return
      bounds.push([stop.latitude, stop.longitude])
      L.circleMarker([stop.latitude, stop.longitude], {
        radius: 12, fillColor: crewColor, fillOpacity: 0.9,
        color: '#fff', weight: 2
      }).bindTooltip(String(stop.order), {
        permanent: true, direction: 'center',
        className: 'leaflet-tooltip-crew-order'
      }).addTo(map)
    })

    if (bounds.length) map.fitBounds(bounds, { padding: [40, 40] })

    return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null } }
  }, [routeData, crewColor])

  const stops = routeData?.stops || []
  const meta = routeData?.meta || {}
  const totalServiceMin = stops.reduce((sum, s) => sum + (s.serviceMinutes || 0), 0)
  const totalDriveMin = stops.reduce((sum, s) => sum + (s.driveTimeMinutes || 0), 0)
  const totalMiles = stops.reduce((sum, s) => sum + (s.driveMiles || 0), 0)

  if (loading) {
    return (
      <div className="rw-card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CircularProgress size={20} style={{ color: 'var(--df-primary)' }} />
          <span style={{ fontSize: 13, color: 'var(--df-ink-3)' }}>Loading route for {crewName || crewId}...</span>
        </div>
      </div>
    )
  }

  if (!routeData || !stops.length) {
    return (
      <div className="rw-card" style={{ padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, color: 'var(--df-ink-3)' }}>
          No route data for {crewName || crewId} on {date}
        </span>
        <button
          onClick={fetchRoute}
          style={{
            padding: '4px 12px', borderRadius: 'var(--df-radius-sm)', border: '1px solid var(--df-line)',
            background: 'var(--df-surface)', color: 'var(--df-ink-2)', cursor: 'pointer', fontSize: 12
          }}
        >Retry</button>
      </div>
    )
  }

  return (
    <div className="rw-card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <svg width="18" height="18" fill="none" stroke={crewColor} strokeWidth="2" viewBox="0 0 24 24"><path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/></svg>
        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--df-ink)' }}>
          {crewName || crewId} — {date}
        </span>
        <button
          onClick={fetchRoute}
          style={{
            padding: '4px 12px', borderRadius: 'var(--df-radius-sm)', border: '1px solid var(--df-line)',
            background: 'var(--df-surface)', color: 'var(--df-ink-2)', cursor: 'pointer', fontSize: 12
          }}
        >Refresh</button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        <span className="rw-pill">{stops.length} stops</span>
        <span className="rw-pill">Service: {totalServiceMin} min</span>
        {totalDriveMin > 0 && <span className="rw-pill">Drive: {Math.round(totalDriveMin)} min</span>}
        {totalMiles > 0 && <span className="rw-pill">{Math.round(totalMiles * 10) / 10} mi</span>}
        <span className="rw-pill" style={{ background: 'var(--df-surface-2)' }}>Mode: {meta.mode || 'unknown'}</span>
        {meta.missingCoordinates > 0 && (
          <span className="rw-pill" style={{ background: 'var(--df-danger-bg)', color: 'var(--df-danger)' }}>
            {meta.missingCoordinates} missing coords
          </span>
        )}
      </div>

      {stops.length >= 2 && (
        <div style={{ ...ROUTE_MAP_STYLE, borderRadius: 'var(--df-radius-sm)', overflow: 'hidden', marginBottom: 16 }}>
          <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
        </div>
      )}

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Property</th>
              <th style={{ width: 80 }}>Service</th>
              <th style={{ width: 80 }}>Drive</th>
              <th style={{ width: 70 }}>Miles</th>
            </tr>
          </thead>
          <tbody>
            {stops.map(stop => (
              <tr key={stop.aspirePropertyId}>
                <td>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 24, height: 24, borderRadius: '50%',
                    color: '#fff', fontWeight: 700, fontSize: 11,
                    background: crewColor
                  }}>{stop.order}</span>
                </td>
                <td>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--df-ink)' }}>{stop.propertyName || '-'}</div>
                  <div style={{ fontSize: 11, color: 'var(--df-ink-3)' }}>
                    {stop.address || ''} {'·'} #{stop.aspirePropertyId}
                  </div>
                </td>
                <td>{stop.serviceMinutes || 0} min</td>
                <td>{stop.driveTimeMinutes != null ? `${Math.round(stop.driveTimeMinutes)} min` : '-'}</td>
                <td>{stop.driveMiles != null ? `${Math.round(stop.driveMiles * 10) / 10}` : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
